import { resolveSymbol } from "@/helpers/tickerHelpers";
import { TimeRangeValue } from "@/types/timeRanges";
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "./baseQueryWithReauth";

export interface TrendingItem {
  name: string;
  posts: number;
  likes: number;
  sentiment: number;
  sentimentCount: number;
}

export interface TrendingResponse {
  data: TrendingItem[];
  meta: {
    page: number;
    limit: number;
    total: number;
    pageCount: number;
  };
}

export interface TrendingParams {
  time_range?: TimeRangeValue;
  sortBy?: "post" | "likes" | "liked" | "sentiment";
  filter: "all" | "following";
  page?: number;
  limit?: number;
  order?: "asc" | "desc";
}

export interface CompanyInfo {
  symbol: string;
  companyName: string;
  exchange: string;
  logoUrl?: string;

  price: number;
  change: number;
  changePercent: number;

  afterHoursPrice?: number;
  afterHoursChange?: number;
  afterHoursChangePercent?: number;

  marketStatus: "open" | "closed";
  marketTime: string;
  isFocused?: boolean;

  sector?: string;
  industry?: string;

  marketCap?: number;
  forwardPE?: number;
  wallStreetTargetPrice?: number;

  description?: string;
  updatedAt?: string;
  webURL?: string;
}

export const financeApi = createApi({
  reducerPath: "financeApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    validateSymbol: builder.mutation({
      query: (body) => ({
        url: "finance/validate-symbol",
        method: "POST",
        body,
      }),
    }),
    getCompanyInfo: builder.query<CompanyInfo, string>({
      query: (symbol) => `/finance/symbol/${resolveSymbol(symbol)}`,
    }),
    followInstrument: builder.mutation<
      { createdAt: string; userId: number; instrumentId: number; message: string },
      string
    >({
      query: (symbol) => ({
        url: `/finance/follow/${symbol}`,
        method: "POST",
      }),
    }),
    unfollowInstrument: builder.mutation<
      { createdAt: string; userId: number; instrumentId: number; message: string },
      string
    >({
      query: (symbol) => ({
        url: `/finance/follow/${symbol}`,
        method: "DELETE",
      }),
    }),
    getTrendingSectors: builder.query<TrendingResponse, TrendingParams>({
      query: (params) => ({
        url: "/finance/trending/sectors",
        params,
      }),
    }),

    getTrendingIndustries: builder.query<TrendingResponse, TrendingParams>({
      query: (params) => ({
        url: "/finance/trending/industries",
        params,
      }),
    }),
  }),
});

export const {
  useValidateSymbolMutation,
  useGetCompanyInfoQuery,
  useFollowInstrumentMutation,
  useUnfollowInstrumentMutation,
  useGetTrendingSectorsQuery,
  useGetTrendingIndustriesQuery,
} = financeApi;
